import { Link } from '@tanstack/react-router';
import { ExternalLink } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import type { ConversationContextDetail } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import { useConversationContext } from '../hooks/use-conversation-context';

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-2 border-b px-4 py-3 last:border-b-0">
      <h3 className="text-muted-foreground text-xs font-medium tracking-wide uppercase">{title}</h3>
      {children}
    </section>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex gap-3 text-sm">
      <dt className="text-muted-foreground w-24 shrink-0">{label}</dt>
      <dd className="min-w-0 flex-1 break-words">{children}</dd>
    </div>
  );
}

const SAFE_URL = /^https?:\/\//i;

/** "Chrome on macOS" from a user agent, enough for a sidebar; the raw string is in the tooltip. */
function deviceOf(userAgent: string | null): string | null {
  if (!userAgent) return null;
  const has = (needle: string) => userAgent.includes(needle);
  const browser = has('Edg/')
    ? 'Edge'
    : has('Chrome/')
      ? 'Chrome'
      : has('Firefox/')
        ? 'Firefox'
        : has('Safari/')
          ? 'Safari'
          : null;
  const os =
    has('iPhone') || has('iPad')
      ? 'iOS'
      : has('Android')
        ? 'Android'
        : has('Mac OS X')
          ? 'macOS'
          : has('Windows')
            ? 'Windows'
            : has('Linux')
              ? 'Linux'
              : null;
  return [browser, os].filter(Boolean).join(' · ') || null;
}

/**
 * Who the shopper is and what they are doing (LC5's context endpoint): page, product, cart,
 * customer, recent orders with their production stage, earlier conversations, attributes.
 */
export function ContextPane({
  storeId,
  conversationId,
}: {
  storeId: string;
  conversationId: string;
}) {
  const context = useConversationContext(storeId, conversationId);
  if (context.isPending) {
    return (
      <div className="space-y-3 p-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }
  if (context.isError)
    return <ErrorState error={context.error} onRetry={() => void context.refetch()} compact />;
  return <ContextDetails storeId={storeId} context={context.data} />;
}

function ContextDetails({
  storeId,
  context,
}: {
  storeId: string;
  context: ConversationContextDetail;
}) {
  const { t } = useTranslation('conversations');
  const format = useFormat();
  const { visitor, page, pages, product, cart, customer, orders, previousConversations } = context;
  const location = visitor
    ? [visitor.city, visitor.region, visitor.country].filter(Boolean).join(', ')
    : '';
  const device = deviceOf(visitor?.userAgent ?? null);
  const attributes = Object.entries(visitor?.attributes ?? {});

  return (
    <div className="text-sm">
      {page.url || page.title ? (
        <Section title={t('context.page')}>
          {page.url && SAFE_URL.test(page.url) ? (
            <a
              href={page.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-start gap-1 break-all underline underline-offset-2"
            >
              {page.title ?? page.url}
              <ExternalLink className="mt-0.5 size-3 shrink-0" aria-hidden />
            </a>
          ) : (
            <p>{page.title}</p>
          )}
          {page.referrer ? (
            <p className="text-muted-foreground text-xs break-all">
              {t('context.referrer', { referrer: page.referrer })}
            </p>
          ) : null}
        </Section>
      ) : null}

      {pages.length > 0 ? (
        <Section title={t('context.pagesThisVisit', { count: pages.length })}>
          <ol className="space-y-1.5">
            {pages.map((visited) => (
              <li key={`${visited.at}:${visited.url}`} className="flex gap-2 text-xs">
                <time
                  dateTime={visited.at}
                  title={format.utc(visited.at)}
                  className="text-muted-foreground w-12 shrink-0 tabular-nums"
                >
                  {format.time(visited.at)}
                </time>
                {SAFE_URL.test(visited.url) ? (
                  <a
                    href={visited.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={visited.url}
                    className="min-w-0 truncate underline-offset-2 hover:underline"
                  >
                    {visited.title ?? visited.url}
                  </a>
                ) : (
                  <span className="min-w-0 truncate">{visited.title ?? visited.url}</span>
                )}
              </li>
            ))}
          </ol>
        </Section>
      ) : null}

      {product ? (
        <Section title={t('context.product')}>
          <div className="flex gap-3">
            {product.featuredMedia ? (
              <img
                src={product.featuredMedia.url}
                alt={product.featuredMedia.alt ?? ''}
                className="size-14 shrink-0 rounded-md border object-cover"
              />
            ) : null}
            <div className="min-w-0 space-y-1">
              <p className="font-medium">{product.title}</p>
              {product.price ? (
                <p className="text-xs">
                  {product.price.min.amount === product.price.max.amount
                    ? format.money(product.price.min)
                    : `${format.money(product.price.min)} – ${format.money(product.price.max)}`}
                </p>
              ) : null}
              <Badge variant="outline" className="h-5 text-[10px]">
                {t(`availability.${product.availability}`)}
              </Badge>
              {product.leadTimeDaysMin !== null ? (
                <p className="text-muted-foreground text-xs">
                  {t('context.leadTime', {
                    min: product.leadTimeDaysMin,
                    max: product.leadTimeDaysMax ?? product.leadTimeDaysMin,
                  })}
                </p>
              ) : null}
            </div>
          </div>
        </Section>
      ) : null}

      {cart ? (
        <Section title={t('context.cart')}>
          <ul className="space-y-1">
            {cart.lines.map((line) => (
              <li key={line.variantId} className="flex justify-between gap-2 text-xs">
                <span className="min-w-0 truncate">
                  {line.quantity} × {line.title}
                  {line.variantTitle ? (
                    <span className="text-muted-foreground"> · {line.variantTitle}</span>
                  ) : null}
                  {!line.available ? (
                    <span className="text-destructive"> · {t('context.unavailable')}</span>
                  ) : null}
                </span>
                <span className="shrink-0 tabular-nums">{format.money(line.linePrice)}</span>
              </li>
            ))}
          </ul>
          <p className="flex justify-between border-t pt-1 text-xs font-medium">
            <span>{t('context.cartTotal')}</span>
            <span className="tabular-nums">{format.money(cart.total)}</span>
          </p>
        </Section>
      ) : null}

      {customer ? (
        <Section title={t('context.customer')}>
          <dl className="space-y-1">
            <Row label={t('context.name')}>
              <Link
                to="/s/$storeId/customers/$customerId"
                params={{ storeId, customerId: customer.id }}
                className="underline underline-offset-2"
              >
                {[customer.firstName, customer.lastName].filter(Boolean).join(' ') ||
                  customer.email}
              </Link>
            </Row>
            <Row label={t('context.email')}>{customer.email}</Row>
            {customer.phone ? <Row label={t('context.phone')}>{customer.phone}</Row> : null}
            <Row label={t('context.spent')}>{format.money(customer.totalSpent)}</Row>
            <Row label={t('context.orders')}>{customer.ordersCount}</Row>
            {customer.wishlistCount > 0 ? (
              <Row label={t('context.wishlist')}>{customer.wishlistCount}</Row>
            ) : null}
          </dl>
          {customer.tags.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {customer.tags.map((tag) => (
                <Badge key={tag} variant="secondary" className="h-5 text-[10px] font-normal">
                  {tag}
                </Badge>
              ))}
            </div>
          ) : null}
          {customer.notes ? (
            <p className="bg-muted rounded-md p-2 text-xs whitespace-pre-wrap">{customer.notes}</p>
          ) : null}
        </Section>
      ) : null}

      {orders.length > 0 ? (
        <Section title={t('context.recentOrders')}>
          <ul className="space-y-2">
            {orders.map((order) => (
              <li key={order.id} className="space-y-0.5">
                <div className="flex items-baseline justify-between gap-2">
                  <Link
                    to="/s/$storeId/orders/$orderId"
                    params={{ storeId, orderId: order.id }}
                    className="font-medium underline underline-offset-2"
                  >
                    {order.name}
                  </Link>
                  <span className="text-xs tabular-nums">{format.money(order.total)}</span>
                </div>
                <p className="text-muted-foreground text-xs">
                  {format.date(order.placedAt)} · {t(`orderStatus.${order.status}`)}
                  {order.workflow?.stageName ? ` · ${order.workflow.stageName}` : ''}
                </p>
                {order.approvalStatus === 'awaiting_design' ||
                order.approvalStatus === 'design_sent' ? (
                  <Badge variant="outline" className="border-warning h-5 text-[10px]">
                    {t('context.awaitingApproval')}
                  </Badge>
                ) : null}
              </li>
            ))}
          </ul>
        </Section>
      ) : null}

      {visitor ? (
        <Section title={t('context.visitor')}>
          <dl className="space-y-1">
            <Row label={t('context.status')}>
              <span className="inline-flex items-center gap-1.5">
                <span
                  className={
                    visitor.online
                      ? 'bg-success size-2 rounded-full'
                      : 'bg-muted-foreground/40 size-2 rounded-full'
                  }
                  aria-hidden
                />
                {visitor.online
                  ? t('context.online')
                  : t('context.lastSeen', { when: format.relative(visitor.lastSeenAt) })}
              </span>
            </Row>
            {visitor.displayName ? (
              <Row label={t('context.name')}>{visitor.displayName}</Row>
            ) : null}
            {visitor.email ? <Row label={t('context.email')}>{visitor.email}</Row> : null}
            {location ? <Row label={t('context.location')}>{location}</Row> : null}
            {visitor.locale ? <Row label={t('context.locale')}>{visitor.locale}</Row> : null}
            {device ? (
              <Row label={t('context.device')}>
                <span title={visitor.userAgent ?? undefined}>{device}</span>
              </Row>
            ) : null}
            <Row label={t('context.visits')}>{visitor.visitsCount}</Row>
            <Row label={t('context.firstSeen')}>{format.date(visitor.firstSeenAt)}</Row>
            {attributes.map(([key, value]) => (
              <Row key={key} label={key}>
                {value}
              </Row>
            ))}
          </dl>
        </Section>
      ) : null}

      {previousConversations.length > 0 ? (
        <Section title={t('context.previous')}>
          <ul className="space-y-1">
            {previousConversations.map((previous) => (
              <li key={previous.id} className="flex justify-between gap-2 text-xs">
                <Link
                  to="/org/inbox/$storeId/$conversationId"
                  params={{ storeId, conversationId: previous.id }}
                  search={{ status: previous.status === 'open' ? undefined : previous.status }}
                  className="underline underline-offset-2"
                >
                  #{previous.number} · {t(`kind.${previous.kind}`)}
                </Link>
                <span className="text-muted-foreground">
                  {t(`status.${previous.status}`)} · {format.date(previous.createdAt)}
                </span>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}
    </div>
  );
}
